import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTrash, FaPlus, FaDatabase, FaHistory, FaRedo, FaArrowUp, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import ListsManager from '../services/ListsManager';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import logoImg from '../assets/images/ReactFirstR.png';
import CitedAnswer from '../components/CitedAnswer';

const SUGGESTIONS = [
  { en: "What was the most damaging post last month?", he: "מה הפוסט המזיק ביותר בחודש האחרון?" },
  { en: "Show me the distribution of posts over time on each platform", he: "הראה לי התפלגות פוסטים לאורך זמן בכל פלטפורמה" },
  { en: "Show me daily sentiment score on TikTok for previous 4 weeks", he: "הראה לי ציון סנטימנט יומי בטיקטוק ב-4 השבועות האחרונים" },
];

export default function ChatbotScreen() {
  const { theme } = useTheme();
  const { activeOrg } = useOrg();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';

  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [reasoningStage, setReasoningStage] = useState('');
  const [streamingAnswer, setStreamingAnswer] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [citations, setCitations] = useState([]);
  const [citationsExpanded, setCitationsExpanded] = useState(false);
  // Resolved inline citation sources ([{token,type,id,table}]) from the backend.
  // Inline chips are parsed from the answer text in CitedAnswer; this is kept for
  // future per-source enrichment (snippets/urls / deep-link resolution).
  const [sourceCitations, setSourceCitations] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Resolve inline citation refs to source records (for drill-down). Memoized on
  // org so CitedAnswer doesn't re-resolve every render.
  const resolveCitations = useCallback(
    (refs) => ListsManager.resolveChatCitations(activeOrg?.id, refs),
    [activeOrg?.id]
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, reasoningStage, streamingAnswer]);

  useEffect(() => {
    if (activeOrg?.id) loadConversations();
  }, [activeOrg?.id]);

  const loadConversations = async () => {
    const res = await ListsManager.fetchChatConversations(activeOrg?.id);
    if (res?.conversations) setConversations(res.conversations);
  };

  const loadConversation = async (convId) => {
    setActiveConversationId(convId);
    const res = await ListsManager.fetchChatConversation(convId, activeOrg?.id);
    if (res?.messages) setMessages(res.messages);
    setReasoningStage('');
    setStreamingAnswer('');
    setSuggestions([]);
    setCitations([]);
    setSourceCitations([]);
  };

  const startNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setReasoningStage('');
    setStreamingAnswer('');
    setSuggestions([]);
    setCitations([]);
    setSourceCitations([]);
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    await ListsManager.deleteChatConversation(convId, activeOrg?.id);
    if (activeConversationId === convId) startNewConversation();
    loadConversations();
  };

  const sendMessage = async (text) => {
    const userMessage = (text || input).trim();
    if (!userMessage || isStreaming) return;

    setInput('');
    setIsStreaming(true);
    setReasoningStage('');

    setStreamingAnswer('');
    setSuggestions([]);
    setCitations([]);
    setSourceCitations([]);
    setMessages(prev => [...prev, { role: 'user', content: userMessage, created_at: new Date().toISOString() }]);

    try {
      const token = await ListsManager.getAuthToken();
      const response = await fetch(`${ListsManager.baseUrl}/chatbot/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: userMessage, conversation_id: activeConversationId, org_id: activeOrg?.id }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalAnswer = '';
      let accumulatedAnswer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'conversation_id') {
              if (!activeConversationId) setActiveConversationId(event.content);
            } else if (event.type === 'thinking') {
              setReasoningStage('Interpreting question...');
            } else if (event.type === 'plan') {
              setReasoningStage('Planning research...');
            } else if (event.type === 'research') {
              setReasoningStage('Exploring data...');
            } else if (event.type === 'subagent_done') {
              setReasoningStage('Analyzing results...');
            } else if (event.type === 'findings') {
              setReasoningStage('Processing findings...');
            } else if (event.type === 'answer_delta') {
              accumulatedAnswer += event.content;
              setStreamingAnswer(accumulatedAnswer);
            } else if (event.type === 'answer') {
              finalAnswer = event.content;
              setReasoningStage('');
            } else if (event.type === 'suggestions') {
              try {
                const parsed = JSON.parse(event.content);
                setSuggestions(parsed);
              } catch (e) { /* skip */ }
            } else if (event.type === 'citations') {
              try {
                const parsed = JSON.parse(event.content);
                setCitations(parsed);
              } catch (e) { /* skip */ }
            } else if (event.type === 'source_citations') {
              try {
                const parsed = JSON.parse(event.content);
                setSourceCitations(parsed);
              } catch (e) { /* skip */ }
            } else if (event.type === 'done' || event.type === 'error') {
              if (event.type === 'error') finalAnswer = event.content;
            }
          } catch (e) { /* skip */ }
        }
      }

      if (finalAnswer) {
        setMessages(prev => [...prev, { role: 'assistant', content: finalAnswer, created_at: new Date().toISOString() }]);
        setStreamingAnswer('');
        setReasoningStage('');
      }
      loadConversations();
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'An error occurred. Please try again.', created_at: new Date().toISOString() }]);
      setReasoningStage('');
      setStreamingAnswer('');
    } finally {
      setIsStreaming(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const isEmptyState = messages.length === 0 && !isStreaming;

  const renderReasoningBlock = () => {
    if (!reasoningStage) return null;

    return (
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px' }}>
        <img src={logoImg} alt="" style={{ width: 20, height: 20, objectFit: 'contain', animation: 'breathe 1.5s ease-in-out infinite' }} />
        <span style={{ fontSize: 13, fontWeight: 500, color: '#6b7280' }}>
          {reasoningStage}
        </span>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#0288d1', animation: 'pulse 1.5s infinite' }} />
      </div>
    );
  };

  const renderStreamingAnswer = () => {
    if (!streamingAnswer) return null;

    return (
      <div style={{ marginBottom: 24, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <img src={logoImg} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, padding: '4px 0', color: '#1f2937', whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 15 }}>
            <CitedAnswer text={streamingAnswer} isRTL={isRTL} showSources={false} resolve={resolveCitations} />
            <span style={{ animation: 'blink 1s infinite' }}>|</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSuggestions = () => {
    if (!suggestions.length || isStreaming) return null;

    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20, marginTop: -8 }}>
        {suggestions.map((s, idx) => (
          <button
            key={idx}
            onClick={() => sendMessage(s)}
            style={{
              padding: '8px 14px',
              borderRadius: 20,
              border: '1px solid #e5e7eb',
              background: '#fff',
              color: '#374151',
              fontSize: 12,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7dd3fc'; e.currentTarget.style.background = '#f0f9ff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
          >
            {s}
          </button>
        ))}
      </div>
    );
  };

  const renderCitations = () => {
    if (!citations.length || isStreaming) return null;

    const totalRows = citations.reduce((sum, c) => sum + (c.row_count || 0), 0);

    return (
      <div style={{ marginBottom: 20, marginTop: -8 }}>
        <div
          onClick={() => setCitationsExpanded(!citationsExpanded)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#9ca3af' }}
        >
          <FaDatabase size={10} />
          <span>Sources: {citations.length} queries, {totalRows} rows</span>
          {citationsExpanded ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
        </div>
        {citationsExpanded && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 8, fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
            {citations.map((c, idx) => (
              <div key={idx} style={{ marginBottom: 4 }}>
                {c.sql?.slice(0, 120)} ({c.row_count} rows)
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMessage = (msg, idx) => {
    const isUser = msg.role === 'user';
    return (
      <div key={idx} style={{ marginBottom: 24, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {!isUser && (
            <img src={logoImg} alt="" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0, marginTop: 2 }} />
          )}
          <div style={{ flex: 1, maxWidth: isUser ? '80%' : '100%', marginLeft: isUser ? 'auto' : 0 }}>
            {isUser && (
              <div style={{ textAlign: isRTL ? 'left' : 'right', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>You</span>
              </div>
            )}
            <div style={{
              padding: isUser ? '10px 16px' : '4px 0',
              borderRadius: isUser ? 20 : 0,
              background: isUser ? '#f3f4f6' : 'transparent',
              color: '#1f2937',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              fontSize: 15,
              ...(isUser ? { display: 'inline-block', float: isRTL ? 'left' : 'right' } : {}),
            }}>
              {isUser ? msg.content : <CitedAnswer text={msg.content} isRTL={isRTL} resolve={resolveCitations} />}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: '#ffffff', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Sidebar */}
      <div style={{ width: 260, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
        {/* Sidebar Header */}
        <div style={{ padding: '16px 12px', height: 68, display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #e5e7eb' }}>
          <button onClick={startNewConversation} style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="New chat">
            <FaPlus size={12} color="#6b7280" />
          </button>
          <button style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="History">
            <FaHistory size={12} color="#6b7280" />
          </button>
        </div>

        {/* Conversation List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => loadConversation(conv.id)}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                marginBottom: 2,
                cursor: 'pointer',
                background: activeConversationId === conv.id ? '#e8ecf4' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (activeConversationId !== conv.id) e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { if (activeConversationId !== conv.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {conv.title || 'Untitled'}
              </span>
              <FaTrash size={10} style={{ color: '#9ca3af', cursor: 'pointer', opacity: 0.5, flexShrink: 0, marginLeft: 8 }}
                onClick={(e) => deleteConversation(conv.id, e)} />
            </div>
          ))}
        </div>
      </div>

      {/* Main Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '14px 24px', height: 68, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={logoImg} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
          <span style={{ fontWeight: 600, fontSize: 17, color: '#111827' }}>
            {t('app.chatbot.title', 'ReactFirst AI Chat')}
          </span>
          <span style={{ fontSize: 11, color: '#0288d1', background: '#e0f7fa', padding: '2px 8px', borderRadius: 4, fontWeight: 500 }}>Beta</span>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {isEmptyState ? (
            /* Empty State — centered like the screenshot */
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
              {/* Logo + Heading */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, marginBottom: 40 }}>
                <img src={logoImg} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                <h1 style={{ fontSize: 28, fontWeight: 600, color: '#111827', margin: 0, textAlign: 'center' }}>
                  {t('app.chatbot.welcome', 'What can I help with?')}
                </h1>
              </div>

              {/* Input */}
              <div style={{ width: '100%', maxWidth: 600, marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 24, padding: '4px 6px 4px 20px', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('app.chatbot.placeholder', 'Ask anything...')}
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: '#374151', padding: '12px 0', background: 'transparent' }}
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim()}
                    style={{
                      width: 36, height: 36, borderRadius: '50%', border: 'none',
                      background: input.trim() ? '#0288d1' : '#e5e7eb',
                      color: '#fff', cursor: input.trim() ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'background 0.2s',
                    }}
                  >
                    <FaArrowUp size={14} />
                  </button>
                </div>
              </div>

              {/* Suggestion Cards */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 700 }}>
                {SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(isRTL ? s.he : s.en)}
                    style={{
                      flex: '1 1 200px',
                      maxWidth: 220,
                      padding: '14px 16px',
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      color: '#374151',
                      fontSize: 13,
                      lineHeight: 1.5,
                      textAlign: isRTL ? 'right' : 'left',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7dd3fc'; e.currentTarget.style.background = '#f0f9ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
                  >
                    {isRTL ? s.he : s.en}
                  </button>
                ))}
              </div>

              {/* More Ideas */}
              <button style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer' }}>
                <FaRedo size={11} />
                {t('app.chatbot.moreIdeas', 'More Ideas')}
              </button>
            </div>
          ) : (
            /* Conversation View */
            <div style={{ flex: 1, padding: '24px 32px', maxWidth: 800, margin: '0 auto', width: '100%' }}>
              {messages.map(renderMessage)}

              {/* Reasoning block — replaces old streaming events */}
              {isStreaming && renderReasoningBlock()}

              {/* Streaming answer with typewriter */}
              {renderStreamingAnswer()}

              {/* Initial thinking indicator */}
              {isStreaming && !reasoningStage && !streamingAnswer && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: '#9ca3af', fontSize: 14 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0288d1', animation: 'pulse 1.5s infinite' }} />
                  {t('app.chatbot.thinking', 'Thinking...')}
                </div>
              )}

              {/* Follow-up suggestions */}
              {renderSuggestions()}

              {/* Citations/Sources */}
              {renderCitations()}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Bottom Input (shown when in conversation) */}
        {!isEmptyState && (
          <div style={{ padding: '16px 32px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 24, padding: '4px 6px 4px 20px', background: '#fff' }}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('app.chatbot.placeholder', 'Ask anything...')}
                  disabled={isStreaming}
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: '#374151', padding: '12px 0', background: 'transparent' }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isStreaming}
                  style={{
                    width: 36, height: 36, borderRadius: '50%', border: 'none',
                    background: input.trim() && !isStreaming ? '#0288d1' : '#e5e7eb',
                    color: '#fff', cursor: input.trim() && !isStreaming ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s',
                  }}
                >
                  <FaArrowUp size={14} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        @keyframes breathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
      `}} />
    </div>
  );
}
